import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../../../components/button/button";
import RadioGroup from "../../../../../../components/radio-group/radio-group";
import type { RadioInputConfig } from "../../../../../../components/radio-input/radio-input.types";
import { openXShareIntent } from "../../../../../../lib/x-share";
import {
	addPostRequest,
	varReviewRequest,
} from "../../../../../../redux/broadcast/broadcast.actions";
import {
	selectBroadcastId,
	selectIsMatchInProgress,
} from "../../../../../../redux/broadcast/broadcast.selectors";
import type { KeyMomentType } from "../../../../../../types/post.types";
import UpdatesSocial from "../../components/social/updates-social";
import UpdatesTextArea from "../../components/updates-textarea/updates-textarea";
import useSocialShare from "../../hooks/use-social-share";
import useUpdatesText from "../../hooks/use-updates-text";

type UpdatesType = "normal" | "chance" | "big-chance" | "penalty" | "var";

const KEY_MOMENT_TYPE_BY_UPDATE_TYPE: Partial<
	Record<UpdatesType, KeyMomentType>
> = {
	chance: "chance",
	"big-chance": "big_chance",
};

const StandardUpdateControls = () => {
	const [updateType, setUpdateType] = useState<UpdatesType>("normal");
	const { updateText, setUpdateText } = useUpdatesText();

	const dispatch = useDispatch();

	const broadcastId = useSelector(selectBroadcastId);
	const isMatchInProgress = useSelector(selectIsMatchInProgress);
	const social = useSocialShare();

	useEffect(() => {
		if (!isMatchInProgress && updateType !== "normal") {
			setUpdateType("normal");
		}
	}, [isMatchInProgress, updateType]);

	const handlePostUpdate = () => {
		const text = updateText?.trim();
		if (!text || !broadcastId) return;

		dispatch(
			addPostRequest({
				broadcastId,
				text,
				keyMomentType: KEY_MOMENT_TYPE_BY_UPDATE_TYPE[updateType],
				postType:
					updateType === "var"
						? "var"
						: updateType === "penalty"
							? "penalty"
							: undefined,
				share: social.blueSky,
				hashtags: social.parsedHashtags,
			}),
		);

		if (social.postToX) {
			openXShareIntent(text, broadcastId, social.parsedHashtags);
		}

		if (updateType === "var") {
			dispatch(varReviewRequest({ broadcastId, active: false }));
		}

		setUpdateText("");
		setUpdateType("normal");
		social.reset();
	};

	const radioGroupConfig = useMemo((): RadioInputConfig[] => {
		return [
			{
				id: "normal",
				label: "Normal",
				disabled: false,
			},
			{
				id: "chance",
				label: "Chance",
				disabled: !isMatchInProgress,
			},
			{
				id: "big-chance",
				label: "Big chance",
				disabled: !isMatchInProgress,
			},
			{
				id: "penalty",
				label: "Penalty",
				disabled: !isMatchInProgress,
			},
			{
				id: "var",
				label: "VAR decision",
				disabled: !isMatchInProgress,
			},
		];
	}, [isMatchInProgress]);

	return (
		<>
			<div style={{ marginBottom: "1.2rem" }}>
				<RadioGroup
					items={radioGroupConfig}
					activeId={updateType}
					onChange={(id) => setUpdateType(id as UpdatesType)}
				/>
			</div>
			<UpdatesTextArea
				value={updateText}
				onChange={(value) => setUpdateText(value)}
				onSubmit={handlePostUpdate}
			/>
			<div
				style={{
					display: "flex",
					alignItems: "flex-end",
				}}
			>
				<UpdatesSocial
					blueSky={social.blueSky}
					onBlueSkyChange={social.setBlueSky}
					postToX={social.postToX}
					onPostToXChange={social.setPostToX}
					hashtags={social.hashtags}
					onHashtagsChange={social.setHashtags}
				/>
				<Button disabled={updateText?.trim() === ""} onClick={handlePostUpdate}>
					Post update
				</Button>
			</div>
		</>
	);
};

export default StandardUpdateControls;
